/**
 * Type-preserving deep clone for relation-cache snapshots (ADR-012).
 *
 * `#store` hands the consumed-`$attributes` snapshot to the prepare-middleware
 * pipeline, but `setRelated` has already handed the live related instance to the
 * caller. A shallow copy shares every nested object by reference, so a prepare
 * handler that mutates a nested value in place (`context.value.meta.secret = …`)
 * writes straight through into the live model the caller is holding. Deep-cloning
 * the snapshot before middleware runs makes the copy's depth the isolation
 * boundary, so no handler — however it mutates — can reach the live row.
 *
 * `structuredClone` is deliberately NOT used: it silently flattens a Luxon
 * `DateTime` into a method-less plain object (the `isLuxonDateTime` flag survives,
 * so it still passes `DateTime.isDateTime`), which then crashes `encodeCacheValue`
 * when it calls `dateTime.toMillis()`. This clone instead recurses the in-place
 * mutation vectors (plain objects, arrays, `Map`, `Set`, `Date`) and keeps the
 * encoder's immutable rich leaves (Luxon `DateTime`, typed arrays, `bigint`, and
 * any other class instance) by reference — those are never mutated in place and
 * are not safely structurally cloneable.
 *
 * @module
 */

/**
 * Recursively clones a single snapshot value, isolating the in-place mutation
 * vectors while preserving the encoder's rich leaf types by reference.
 *
 * @param value - The value to clone.
 * @returns A mutation-isolated copy, or the original for an immutable leaf.
 */
const cloneValue = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object') {
    return value
  }
  if (value instanceof Date) {
    return new Date(value.getTime())
  }
  if (value instanceof Map) {
    const clone = new Map<unknown, unknown>()
    for (const [key, entry] of value) {
      clone.set(key, cloneValue(entry))
    }
    return clone
  }
  if (value instanceof Set) {
    const clone = new Set<unknown>()
    for (const entry of value) {
      clone.add(cloneValue(entry))
    }
    return clone
  }
  if (Array.isArray(value)) {
    return value.map(cloneValue)
  }
  const prototype = Object.getPrototypeOf(value) as object | null
  if (prototype !== Object.prototype && prototype !== null) {
    // Class instance (Luxon DateTime, typed array, …): an immutable encoder leaf
    // kept by reference — structural cloning would corrupt it.
    return value
  }
  const clone: Record<string, unknown> = {}
  for (const key of Object.keys(value as Record<string, unknown>)) {
    clone[key] = cloneValue((value as Record<string, unknown>)[key])
  }
  return clone
}

/**
 * Deep-clones a consumed-`$attributes` snapshot so the prepare-middleware pipeline
 * can never mutate the live related instance the caller already holds (ADR-012).
 *
 * @param snapshot - The post-`consume` attribute snapshot to isolate.
 * @returns A deep copy safe to hand to user-supplied middleware.
 */
export const deepCloneSnapshot = (snapshot: Record<string, unknown>): Record<string, unknown> =>
  cloneValue(snapshot) as Record<string, unknown>
